import * as React from 'react';
import { RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
  useGenerateMyApplePayCsr,
  useUploadMyAppleCertificate,
  useUploadMyApplePayCertificates,
} from '@/lib/api/apple-pay-registrations';
import { ExpiryBadge } from '@/components/apple-pay/byok-ui';
import {
  CsrCertificateSetup,
  downloadCsrFile,
  type CsrSetupState,
} from '@/components/apple-pay/CsrCertificateSetup';
import { P12UploadForm, type P12UploadPayload } from '@/components/apple-pay/P12UploadForm';
import type { MyApplePayRegistration } from './types';

const BT_MAX_ACTIVE_CERTIFICATES = 2;

export function MerchantCertificates({ registration }: { registration: MyApplePayRegistration }) {
  const generateCsr = useGenerateMyApplePayCsr();
  const uploadCert = useUploadMyAppleCertificate();
  const uploadP12 = useUploadMyApplePayCertificates();
  const [rotateTarget, setRotateTarget] = React.useState<string | null>(null);
  // 'csr' = guided flow (we hold the keys); 'p12' = user already has certs + keys.
  const [mode, setMode] = React.useState<'csr' | 'p12'>('csr');

  const certs = registration.certificates;
  const atCap = certs.length >= BT_MAX_ACTIVE_CERTIFICATES;
  const setup = (registration.certificateSetup ?? []) as CsrSetupState[];
  const setupInProgress = setup.length > 0;
  const busy = generateCsr.isPending || uploadCert.isPending || uploadP12.isPending;

  const handleDownloadCsr = async (type: CsrSetupState['type']) => {
    const result = await generateCsr.mutateAsync({
      registrationId: registration.id,
      certificateType: type,
    });
    downloadCsrFile(result.csrPem, result.fileName);
  };

  const handleUploadCert = async (type: CsrSetupState['type'], certificateBase64: string) => {
    const result = await uploadCert.mutateAsync({
      registrationId: registration.id,
      certificateType: type,
      certificate: certificateBase64,
      ...(rotateTarget ? { replaceCertificateId: rotateTarget } : {}),
    });
    if (result.finalized) setRotateTarget(null);
  };

  const handleP12Submit = async (payload: P12UploadPayload) => {
    await uploadP12.mutateAsync({
      registrationId: registration.id,
      ...payload,
      ...(rotateTarget ? { replaceCertificateId: rotateTarget } : {}),
    });
    setRotateTarget(null);
  };

  // The setup panel shows when there is no active certificate yet, when a
  // renewal is in progress, or when a previous setup was left half-done.
  const showSetup = certs.length === 0 || rotateTarget !== null || setupInProgress;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">
        Certificates ({certs.length}/{BT_MAX_ACTIVE_CERTIFICATES})
      </h3>

      {certs.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment cert expiry</TableHead>
              <TableHead>Identity cert expiry</TableHead>
              <TableHead className="w-[120px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certs.map((cert) => (
              <TableRow key={cert.id}>
                <TableCell>
                  <ExpiryBadge value={cert.paymentCertExpiresAt} />
                </TableCell>
                <TableCell>
                  <ExpiryBadge value={cert.identityCertExpiresAt} />
                </TableCell>
                <TableCell className="text-right">
                  {rotateTarget === cert.id ? (
                    <Button variant="outline" size="sm" onClick={() => setRotateTarget(null)}>
                      <X className="mr-1 h-3.5 w-3.5" />
                      Cancel renewal
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={rotateTarget !== null}
                      onClick={() => setRotateTarget(cert.id)}
                    >
                      <RotateCw className="mr-1 h-3.5 w-3.5" />
                      Renew
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {showSetup ? (
        <div className="space-y-2">
          {mode === 'csr' ? (
            <>
              <CsrCertificateSetup
                setup={setup}
                busy={busy}
                replacing={rotateTarget !== null}
                onDownloadCsr={handleDownloadCsr}
                onUploadCert={handleUploadCert}
              />
              <Button
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto px-0 text-xs"
                onClick={() => setMode('p12')}
              >
                Already have certificates (with their private keys)? Upload .p12 files instead
              </Button>
            </>
          ) : (
            <>
              <P12UploadForm busy={busy} replacing={rotateTarget !== null} onSubmit={handleP12Submit} />
              <Button
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto px-0 text-xs"
                onClick={() => setMode('csr')}
              >
                Only have .cer files or nothing yet? Use the guided signing-request flow instead
              </Button>
            </>
          )}
        </div>
      ) : atCap ? (
        <p className="text-muted-foreground text-xs">
          You have the maximum of 2 certificates. Use <strong>Renew</strong> to replace one before it expires.
        </p>
      ) : (
        <p className="text-muted-foreground text-xs">
          Use <strong>Renew</strong> on a certificate to rotate it before expiry.
        </p>
      )}
    </div>
  );
}
